/**
 * A narrow safety net for horizontal strips and product carousels that
 * genuinely SWALLOW the vertical wheel, so customer pages scroll normally.
 *
 * It used to fire on every strip and every product carousel, unconditionally:
 * `preventDefault()` and then vertical scrolling re-implemented by hand. That
 * is not free. Measured on the shop page, pointer over a product card versus
 * over the page background, ten notches of 60px:
 *
 *     over the background        600 / 600px   (100%)
 *     over a product card        450 / 600px   ( 75%)   ← handler on
 *     over a product card        600 / 600px   (100%)   ← handler off
 *
 * A quarter of every scroll, gone — the owner's "on the products it scrolls
 * slowly". The cause is `scrollTop +=` against a root that sets
 * `scroll-behavior: smooth`: each notch RETARGETS the animation still running
 * from the last one, from wherever it has got to rather than from where it
 * was headed, so part of every notch is dropped. And a hand-rolled scroll is
 * a main-thread scroll: no fling, no momentum, no compositor.
 *
 * So it now does two things it did not do before.
 *
 * 1. It asks whether the wheel would actually be swallowed before taking it.
 *    Measured, with the handler off: a strip whose `overflow-y` computes to
 *    `auto` with nothing to scroll chains out to the page perfectly well
 *    (100px per notch) — that half of the folklore is not true on a current
 *    engine. What does NOT chain is a scroll container whose
 *    `overscroll-behavior` is anything but `auto`: 0px per notch, whether the
 *    overflow is `auto` or `hidden`. That is the trap, and only that.
 * 2. When it does forward, it scrolls INSTANTLY, so a CSS `scroll-behavior:
 *    smooth` cannot eat the delta it was handed.
 *
 * Correctness lives in the CSS — see
 * `Pages/Shop/__tests__/wheelIsNeverTrapped.test.js`. This is the net under
 * it, not the floor.
 */

function findVerticalScrollParent(el) {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll')
      && node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }

  const doc = document.scrollingElement || document.documentElement;
  if (doc.scrollHeight > doc.clientHeight + 1) return doc;
  return null;
}

function isHorizontalScrollStrip(el) {
  if (!el || el.nodeType !== 1) return false;
  const { overflowX } = getComputedStyle(el);
  return overflowX === 'auto' || overflowX === 'scroll';
}

function isProductCarouselRoot(el) {
  return el?.getAttribute?.('data-testid') === 'customer-card-carousel';
}

/** Anything but `visible` and `clip` makes a box a scroll container. */
function isScrollContainerValue(value) {
  return value !== 'visible' && value !== 'clip';
}

/**
 * Does this box actually eat a vertical wheel? Three things have to be true
 * together, and any one of them missing means the browser chains to the page
 * on its own and we must keep our hands off:
 *   - it is a scroll container on the vertical axis,
 *   - it has nothing to scroll there, and
 *   - it refuses to chain what it cannot use.
 */
function swallowsVerticalWheel(el) {
  if (!el || el.nodeType !== 1) return false;
  const style = getComputedStyle(el);
  if (!isScrollContainerValue(style.overflowY)) return false;
  if (el.scrollHeight > el.clientHeight + 1) return false;
  const chaining = style.overscrollBehaviorY || style.overscrollBehavior;
  return chaining != null && chaining !== '' && chaining !== 'auto';
}

function findScrollTrapAncestor(target, root, matcher) {
  let node = target;
  while (node && node !== root) {
    if (matcher(node)) return node;
    node = node.parentElement;
  }
  return null;
}

function forwardVerticalScroll(target, root, deltaY) {
  const trap = findScrollTrapAncestor(target, root, (node) => (
    (isHorizontalScrollStrip(node) || isProductCarouselRoot(node))
    && swallowsVerticalWheel(node)
  ));
  if (!trap) return false;

  const scrollParent = findVerticalScrollParent(trap);
  if (!scrollParent) return false;

  // INSTANT. `scrollTop +=` obeys `scroll-behavior: smooth` on the root, and
  // one animation retargeting the next is how a quarter of every notch was
  // being lost.
  try {
    scrollParent.scrollBy({ top: deltaY, left: 0, behavior: 'instant' });
  } catch (e) {
    scrollParent.scrollTop += deltaY;
  }
  return true;
}

export function bindForwardVerticalWheelDelegated(root) {
  if (!root || typeof root.addEventListener !== 'function') return () => {};

  const onWheel = (e) => {
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);
    if (absY === 0 && absX === 0) return;
    if (absY < absX) return;

    if (forwardVerticalScroll(e.target, root, e.deltaY)) {
      e.preventDefault();
    }
  };

  root.addEventListener('wheel', onWheel, { passive: false, capture: true });
  return () => root.removeEventListener('wheel', onWheel, { capture: true });
}

/** Mobile: don't lock vertical pan on horizontal strips / carousels. */
export function bindTouchScrollPassthroughDelegated(root) {
  if (!root || typeof root.addEventListener !== 'function') return () => {};

  const active = new Map();

  const onTouchStart = (e) => {
    for (let i = 0; i < e.changedTouches.length; i += 1) {
      const touch = e.changedTouches[i];
      // Touch keeps the wider net: a horizontal pan lock is a real thing on
      // a strip that CAN scroll sideways, which is a different hazard from
      // the wheel's, and releasing the gesture costs nothing when it is not.
      const trap = findScrollTrapAncestor(e.target, root, (node) => (
        isHorizontalScrollStrip(node) || isProductCarouselRoot(node)
      ));
      if (!trap) continue;
      active.set(touch.identifier, {
        startX: touch.clientX,
        startY: touch.clientY,
        axis: null,
      });
    }
  };

  const onTouchMove = (e) => {
    for (let i = 0; i < e.changedTouches.length; i += 1) {
      const touch = e.changedTouches[i];
      const state = active.get(touch.identifier);
      if (!state) continue;

      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;

      if (!state.axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) continue;
        state.axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
      }

      // Vertical gesture — release so the page scrolls (carousel uses horizontal only).
      if (state.axis === 'y') {
        active.delete(touch.identifier);
      }
    }
  };

  const clear = (e) => {
    for (let i = 0; i < e.changedTouches.length; i += 1) {
      active.delete(e.changedTouches[i].identifier);
    }
  };

  root.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
  root.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
  root.addEventListener('touchend', clear, { passive: true, capture: true });
  root.addEventListener('touchcancel', clear, { passive: true, capture: true });

  return () => {
    root.removeEventListener('touchstart', onTouchStart, { capture: true });
    root.removeEventListener('touchmove', onTouchMove, { capture: true });
    root.removeEventListener('touchend', clear, { capture: true });
    root.removeEventListener('touchcancel', clear, { capture: true });
  };
}

export function bindCustomerScrollPassthrough(root) {
  const cleanWheel = bindForwardVerticalWheelDelegated(root);
  const cleanTouch = bindTouchScrollPassthroughDelegated(root);
  return () => {
    cleanWheel();
    cleanTouch();
  };
}
